import { FaSearch } from "react-icons/fa";
import { RxCross2 } from "react-icons/rx";
import "../commonStyle.css";
import {
  CAvatar,
  CFormInput,
  CInputGroup,
  CInputGroupText,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { UPDATE_REPORTS_USER_LIST } from "../../store/actionTypes";
import { debounce } from "lodash";
import CustomPagination from "../customPagination";
import { BsSortAlphaDown, BsSortAlphaDownAlt } from "react-icons/bs";
import { BsSortNumericDownAlt, BsSortNumericDown } from "react-icons/bs";
import DefaultProfilePic from "../../assets/images/DefaultProfilePic.jpg";
import CIcon from "@coreui/icons-react";
import { cilPeople } from "@coreui/icons";
import ProperSpinner from "../spinner/ProperSpinner";
import {
  dateFormatter,
  getDateAndTimeFormatted,
} from "../../utils/commonFunctions";

/**
 * @function ReportedUserTable
 * @description for ReportedUserTable
 * @returns JSX for ReportedUserTable
 */

const ReportedUserTable = ({ selectedIdHandler }) => {
  const {
    dataList: usersList,
    pageNo,
    totalPage,
    limit,
    searchKey,
    isLoading,
    sortOrder,
  } = useSelector((state) => state.ReportReducer.user.reportedUsers);

  const dispatch = useDispatch();
  const [sortApplied, setSortApplied] = useState("firstName");
  const [inputValue, setInputValue] = useState(searchKey);

  const handlerSort = (sortBy) => {
    setSortApplied(sortBy);
    dispatch({
      type: UPDATE_REPORTS_USER_LIST,
      payload: { sortBy: sortBy, sortOrder: sortOrder === 1 ? -1 : 1 },
    });
  };

  const updatePage = (page) => {
    dispatch({
      type: UPDATE_REPORTS_USER_LIST,
      payload: { pageNo: page },
    });
  };

  const pageLimitHandler = (e) => {
    const currLimit = e?.target?.value || 10;
    dispatch({
      type: UPDATE_REPORTS_USER_LIST,
      payload: { limit: currLimit, pageNo: 1 },
    });
  };

  const handleDebounceFn = (input) => {
    dispatch({
      type: UPDATE_REPORTS_USER_LIST,
      payload: { searchKey: input, pageNo: 1 },
    });
  };
  const deBounceFn = useMemo(() => debounce(handleDebounceFn, 500), []);

  const handleChange = (e) => {
    const text = e.target.value.replace(/[^a-zA-Z\s]/g, "").trimStart();
    setInputValue(text);
    if (text.length == 0 || text.length > 2) {
      deBounceFn(text);
    }
  };

  const handleClearInputField = () => {
    setInputValue("");
    deBounceFn("");
  };

  const renderTableBody = () => {
    return (
      <CTableBody>
        {usersList?.length > 0 &&
          usersList?.map((user, i) => {
            return (
              <CTableRow key={i} className="table-data-text">
                <CTableDataCell className="table-heading-10-cell">
                  <CAvatar
                    size="sm"
                    src={
                      user?.profileImage ? user.profileImage : DefaultProfilePic
                    }
                    style={{ objectFit: "cover" }}
                    status={user?.status === 1 ? "success" : "warning"}
                  />
                </CTableDataCell>
                <CTableDataCell
                  onClick={() => selectedIdHandler(user._id)}
                  className="name-link table-heading-30-cell"
                >
                  {user.firstName + " " + user?.lastName || "N/A"}
                </CTableDataCell>
                <CTableDataCell className="table-heading-20-cell">
                  {user?.reportCount || "N/A"}
                </CTableDataCell>
                <CTableDataCell className="table-heading-20-cell">
                  {getDateAndTimeFormatted(user?.createdAt)}
                </CTableDataCell>
                <CTableDataCell className="table-heading-20-cell">
                  {getDateAndTimeFormatted(user?.reportedAt)}
                </CTableDataCell>
              </CTableRow>
            );
          })}
      </CTableBody>
    );
  };

  const renderPagination = () => {
    return (
      <CustomPagination
        limit={limit}
        limitHanlder={pageLimitHandler}
        pageNo={pageNo}
        pageChangeHanlder={updatePage}
        totalPage={totalPage}
      />
    );
  };

  const renderNoDataFound = () => {
    if (isLoading) {
      return (
        <div className="ndf-container">
          <h3 className="ndf-heading">
            <span>{" Fetching users "}</span> {"..."}
          </h3>
        </div>
      );
    }
    return (
      <div className="ndf-container">
        <h3 className="ndf-heading">
          {"No"}
          <span>{" Users "}</span> {"available !!!"}
        </h3>
      </div>
    );
  };

  const renderView = () => {
    return (
      <div>
        <div className="table-header-container">
          <h3 className="w-50 mb-0 text-primary table-heading">
            {"Reported User List"}
          </h3>
          <div>
            {/* Search section */}
            <CInputGroup className="" style={{ width: "300px" }} size="sm">
              <CInputGroupText className="cursor-pointer">
                <FaSearch />
              </CInputGroupText>
              <CFormInput
                value={inputValue}
                name="text"
                onChange={handleChange}
                placeholder="Search user name"
              />
              {inputValue && (
                <CInputGroupText
                  className="cursor-pointer"
                  onClick={handleClearInputField}
                >
                  <RxCross2 />
                </CInputGroupText>
              )}
            </CInputGroup>
          </div>
        </div>
        <CTable hover responsive>
          <CTableHead className="text-nowrap border">
            <CTableRow className="table-heading-text">
              <CTableHeaderCell className="bg-body-tertiary table-heading-10-cell">
                <CIcon icon={cilPeople} />
              </CTableHeaderCell>
              <CTableHeaderCell
                className={`bg-body-tertiary table-heading-30-cell ${sortApplied === "firstName" ? "sort-highlight" : ""}`}
              >
                {"Full Name"}
                <span onClick={() => handlerSort("firstName")}>
                  {sortOrder === 1 ? (
                    <BsSortAlphaDown className="sort-icons cursor-pointer" />
                  ) : (
                    <BsSortAlphaDownAlt className="sort-icons cursor-pointer" />
                  )}
                </span>
              </CTableHeaderCell>
              <CTableHeaderCell
                className={`bg-body-tertiary table-heading-20-cell ${sortApplied === "reportCount" ? "sort-highlight" : ""}`}
              >
                {"Reported Count"}
                <span onClick={() => handlerSort("reportCount")}>
                  {sortOrder === 1 ? (
                    <BsSortNumericDown className="sort-icons cursor-pointer" />
                  ) : (
                    <BsSortNumericDownAlt className="sort-icons cursor-pointer" />
                  )}
                </span>
              </CTableHeaderCell>
              <CTableHeaderCell
                className={`bg-body-tertiary table-heading-20-cell ${sortApplied === "createdAt" ? "sort-highlight" : ""}`}
              >
                {"Created At"}
                <span onClick={() => handlerSort("createdAt")}>
                  {sortOrder === 1 ? (
                    <BsSortAlphaDown className="sort-icons cursor-pointer" />
                  ) : (
                    <BsSortAlphaDownAlt className="sort-icons cursor-pointer" />
                  )}
                </span>
              </CTableHeaderCell>
              <CTableHeaderCell
                className={`bg-body-tertiary table-heading-20-cell ${sortApplied === "reportedAt" ? "sort-highlight" : ""}`}
              >
                {"Reported At"}
                <span onClick={() => handlerSort("reportedAt")}>
                  {sortOrder === 1 ? (
                    <BsSortAlphaDown className="sort-icons cursor-pointer" />
                  ) : (
                    <BsSortAlphaDownAlt className="sort-icons cursor-pointer" />
                  )}
                </span>
              </CTableHeaderCell>
            </CTableRow>
          </CTableHead>
          {isLoading ? <ProperSpinner /> : renderTableBody()}
        </CTable>
        {(usersList?.length >= 5 || pageNo > 1) && renderPagination()}
        {/* {!isLoading && usersList?.length == 0 && renderNoDataFound()} */}
        {(isLoading || usersList?.length == 0) && renderNoDataFound()}
      </div>
    );
  };
  return <div className="rm-table-container">{renderView()}</div>;
};

export default ReportedUserTable;
